import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ROLES, homePathForRole } from './constants/roles';
import ProtectedRoute from './components/routing/ProtectedRoute';
import Login from './components/Login';
import { ToastContainer } from './components/Toast';
import { ConfirmProvider, setGlobalConfirm, useConfirm } from './ui/ConfirmDialog';
import './App.css';
import './ui/ConfirmDialog.css';

const Dashboard = lazy(() => import('./components/Dashboard'));
const TeacherDashboard = lazy(() => import('./components/TeacherDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const BatchDetailsPage = lazy(() => import('./components/BatchDetailsPage'));
const TeacherBatchDetailsPage = lazy(() => import('./components/TeacherBatchDetailsPage'));
const OneToOneBatchManagement = lazy(() => import('./components/OneToOneBatchManagement'));
const StudentAnalyticsDashboard = lazy(() => import('./components/StudentAnalyticsDashboard'));
const PlatformConsole = lazy(() => import('./features/platform/PlatformConsole'));
const ResourcesHome = lazy(() => import('./components/ResourcesHome'));
const StudentAssessmentView = lazy(() => import('./components/StudentAssessmentView'));
const StudentAssessmentResults = lazy(() => import('./components/StudentAssessmentResults'));
const ZoomLiveClassroom = lazy(() => import('./components/ZoomLiveClassroom'));

const LoadingSpinner = () => (
  <div className="lms-loading-screen">
    <div className="loader" />
    <p>Loading…</p>
  </div>
);

function ConfirmBridge() {
  const confirm = useConfirm();
  useEffect(() => {
    setGlobalConfirm(confirm);
    return () => setGlobalConfirm(null);
  }, [confirm]);
  return null;
}

function LoginRoute() {
  const { isAuthenticated, user, bootstrapping } = useAuth();
  if (bootstrapping) return <LoadingSpinner />;
  if (isAuthenticated) return <Navigate to={homePathForRole(user.role)} replace />;
  return <Login />;
}

function HomeRoute() {
  const { isAuthenticated, user, bootstrapping } = useAuth();
  if (bootstrapping) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(user.role)} replace />;
}

function SessionBanner() {
  const { sessionWarning, dismissSessionWarning, isAuthenticated } = useAuth();
  if (!sessionWarning || !isAuthenticated) return null;
  return (
    <div className="session-warning-banner">
      {sessionWarning.message}
      <button type="button" onClick={dismissSessionWarning} className="session-warning-dismiss">
        Dismiss
      </button>
    </div>
  );
}

function AppRoutes() {
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user?.role === 'student') {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => document.body.classList.remove('dark-mode');
  }, [user]);

  return (
    <div className="App">
      <ConfirmBridge />
      <ToastContainer />
      <SessionBanner />
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/" element={<HomeRoute />} />
        <Route path="/mentor" element={<Navigate to="/teacher" replace />} />

        <Route
          path="/live/:meetingId"
          element={
            <ProtectedRoute roles={[ROLES.TEACHER, ROLES.ADMIN, ROLES.STUDENT]}>
              <Suspense fallback={<LoadingSpinner />}>
                <ZoomLiveClassroom user={user} />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute roles={[ROLES.STUDENT]}>
              <Suspense fallback={<LoadingSpinner />}>
                <Dashboard user={user} onLogout={logout} />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher"
          element={
            <ProtectedRoute roles={[ROLES.TEACHER]}>
              <Suspense fallback={<LoadingSpinner />}>
                <TeacherDashboard user={user} onLogout={logout} />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/batch/:batchId"
          element={
            <ProtectedRoute roles={[ROLES.TEACHER]}>
              <Suspense fallback={<LoadingSpinner />}>
                <TeacherBatchDetailsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <AdminDashboard user={user} onLogout={logout} />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/batch/:batchId"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <BatchDetailsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/one-to-one-batch/:batchId"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <OneToOneBatchManagement />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform"
          element={
            <ProtectedRoute roles={[ROLES.PLATFORM_ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <PlatformConsole user={user} onLogout={logout} />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute roles={[ROLES.STUDENT]}>
              <Suspense fallback={<LoadingSpinner />}>
                <StudentAnalyticsDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/resources"
          element={
            <ProtectedRoute roles={[ROLES.STUDENT, ROLES.TEACHER, ROLES.ADMIN]}>
              <Suspense fallback={<LoadingSpinner />}>
                <ResourcesHome user={user} onLogout={logout} />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/assessment/:assessmentId"
          element={
            <ProtectedRoute roles={[ROLES.STUDENT]}>
              <Suspense fallback={<LoadingSpinner />}>
                <StudentAssessmentView />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/assessment/results/:attemptId"
          element={
            <ProtectedRoute roles={[ROLES.STUDENT]}>
              <Suspense fallback={<LoadingSpinner />}>
                <StudentAssessmentResults />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ConfirmProvider>
          <AppRoutes />
        </ConfirmProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
